import { FC, useEffect, useState } from 'react'
import { PageTitle } from '../../../../_metronic/layout/core'
import { AvailableSellersTab } from './components/AvailableSellersTab'
import { QueueTab } from './components/QueueTab'
import { InServiceTab } from './components/InServiceTab'
import { HistoryTab } from './components/HistoryTab'
import { NoSaleReasonsTab } from './components/NoSaleReasonsTab'
import { ConferenceTab } from './components/ConferenceTab'
import { UsageInstructions } from './components/UsageInstructions'
import useAuth from '../../../hooks/useAuth'
import { getSellerConfig } from './services'
import io from 'socket.io-client'
import { useSellerQueuePluginspaceId } from './hooks/useSellerQueuePluginspaceId'

type TabKey = 'available' | 'queue' | 'inservice' | 'history' | 'conference' | 'reasons'

const SellerQueueContent: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = useSellerQueuePluginspaceId()
  
  const [activeTab, setActiveTab] = useState<TabKey>('queue')
  const [refreshKey, setRefreshKey] = useState(0)
  const [coupledQueueView, setCoupledQueueView] = useState(false)

  const normalizedProfile = String(loginInfo?.profile || '').toLowerCase()
  const isPrivileged =
    normalizedProfile === 'admin' ||
    normalizedProfile === 'owner' ||
    normalizedProfile.includes('manager')

  const handleQueueChanged = () => {
    setRefreshKey((prev) => prev + 1)
  }

  const handleAttendanceStarted = () => {
    setRefreshKey((prev) => prev + 1)
    if (!coupledQueueView) {
      setActiveTab('inservice')
    }
  }

  const loadCoupledViewFlag = async () => {
    try {
      const config = await getSellerConfig()
      setCoupledQueueView(!!config?.coupledQueueView)
    } catch (error) {
      setCoupledQueueView(false)
    }
  }

  useEffect(() => {
    loadCoupledViewFlag()
  }, [])

  useEffect(() => {
    if (activeTab === 'queue') {
      loadCoupledViewFlag()
    }
  }, [activeTab])

  useEffect(() => {
    if (!pluginspaceId) return

    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:7004/api'
    const wsUrl = backendUrl.replace('/api', '')

    const socket = io(wsUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      query: {
        userType: 'agent',
        pluginspaceId: pluginspaceId.toString(),
        source: 'admin-seller-queue',
      },
    })

    const handleQueueRealtimeUpdate = (event: any) => {
      if (Number(event?.pluginspaceId) !== Number(pluginspaceId)) return
      window.dispatchEvent(
        new CustomEvent('seller-queue-updated', {
          detail: event,
        })
      )
    }

    socket.on('connect', () => {
      socket.emit('admin-connect-seller-queue', { pluginspaceId })
    })

    socket.on('seller-queue-updated', handleQueueRealtimeUpdate)

    return () => {
      socket.off('seller-queue-updated', handleQueueRealtimeUpdate)
      socket.disconnect()
    }
  }, [pluginspaceId])

  const tabs: Array<{ key: TabKey; label: string; icon: string }> = [
    ...(isPrivileged
      ? [{ key: 'available' as TabKey, label: 'Vendedores Disponíveis', icon: 'fas fa-users' }]
      : []),
    { key: 'queue', label: 'Fila Atual', icon: 'fas fa-list-ol' },
    { key: 'inservice', label: 'Em Atendimento', icon: 'fas fa-phone' },
    { key: 'history', label: 'Histórico', icon: 'fas fa-history' },
    { key: 'conference', label: 'Conferência', icon: 'fas fa-clipboard-check' },
    { key: 'reasons', label: 'Motivos de Não Venda', icon: 'fas fa-chart-pie' },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'available':
        return (
          <AvailableSellersTab
            key={`${activeTab}-${refreshKey}`}
            pluginspaceId={pluginspaceId}
            onSellerAdded={handleQueueChanged}
          />
        )
      case 'queue':
        return (
          <QueueTab
            key={`${activeTab}-${refreshKey}`}
            pluginspaceId={pluginspaceId}
            onQueueChanged={handleQueueChanged}
            onAttendanceStarted={handleAttendanceStarted}
            isPrivileged={isPrivileged}
          />
        )
      case 'inservice':
        return (
          <InServiceTab
            key={`${activeTab}-${refreshKey}`}
            pluginspaceId={pluginspaceId}
            currentUserId={loginInfo?.id || 0}
            isPrivileged={isPrivileged}
            onQueueChanged={handleQueueChanged}
          />
        )
      case 'history':
        return (
          <HistoryTab
            key={`${activeTab}-${refreshKey}`}
            pluginspaceId={pluginspaceId}
            currentUserId={loginInfo?.id || 0}
            isPrivileged={isPrivileged}
          />
        )
      case 'conference':
        return (
          <ConferenceTab
            key={`${activeTab}-${refreshKey}`}
            isPrivileged={isPrivileged}
            pluginspaceId={pluginspaceId}
          />
        )
      case 'reasons':
        return (
          <NoSaleReasonsTab
            key={`${activeTab}-${refreshKey}`}
            pluginspaceId={pluginspaceId}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0'>
        <ul className='nav nav-tabs nav-line-tabs mb-0 flex-wrap' role='tablist'>
          {tabs.map((tab) => (
            <li className='nav-item' key={tab.key} role='presentation'>
              <button
                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                type='button'
                role='tab'
                aria-selected={activeTab === tab.key}
              >
                <i className={`${tab.icon} me-2`}></i>
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className='card-body'>
        {coupledQueueView && activeTab === 'queue' && (
          <div className='alert alert-info d-flex align-items-center mb-4'>
            <i className='fas fa-columns me-2'></i>
            <span>
              Visão acoplada ativa: Fila Atual e Em Atendimento aparecem juntos nesta tela.
            </span>
          </div>
        )}

        <UsageInstructions activeTab={activeTab} isPrivileged={isPrivileged} />
        <div className='tab-content' id='myTabContent'>
          <div
            className='tab-pane fade show active'
            id={`pane_${activeTab}`}
            role='tabpanel'
          >
            {coupledQueueView && activeTab === 'queue' ? (
              <div className='row g-4'>
                <div className='col-12 col-xl-6'>
                  <QueueTab
                    key={`queue-coupled-${refreshKey}`}
                    pluginspaceId={pluginspaceId}
                    onQueueChanged={handleQueueChanged}
                    onAttendanceStarted={handleAttendanceStarted}
                    isPrivileged={isPrivileged}
                  />
                </div>
                <div className='col-12 col-xl-6'>
                  <InServiceTab
                    key={`inservice-coupled-${refreshKey}`}
                    pluginspaceId={pluginspaceId}
                    currentUserId={loginInfo?.id || 0}
                    isPrivileged={isPrivileged}
                    onQueueChanged={handleQueueChanged}
                  />
                </div>
              </div>
            ) : (
              renderTabContent()
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const SellerQueue: FC = () => {
  return (
    <>
      <PageTitle breadcrumbs={[{ title: 'Gestão de Fila', path: '/seller/queue', isActive: true }]}>
        Gestão de Fila de Vendedores
      </PageTitle>
      <SellerQueueContent />
    </>
  )
}

export { SellerQueue }
